import React, { useEffect } from 'react';
import { toast } from 'react-hot-toast';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setModalName, setModalVisible } from '@/lib/slices/modal-slice';
import { setActiveCourseData, setCourseList } from '@/lib/slices/course-slice';
import { resetActiveGroupData } from '@/lib/slices/admin-group-slice';

import CourseCards from '@/common/components/course/CourseCards';

import UserCourseStorageService from '@/services/storage/user-course-storage-service';
import { role } from 'auth-types';
import { CourseEntity } from 'course-types';

export default function AllAdminCoursesView() {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const userRole: role = useAppSelector(() => store.getState().isLoggedIn.role);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    const storedCourses = UserCourseStorageService.getCourseCookie()?.all?.enterprise ?? [];
    dispatch(setCourseList(storedCourses));

    const refreshedCookie = await UserCourseStorageService.retrieveCourseCookie();
    dispatch(setCourseList(refreshedCookie.all?.enterprise ?? []));
  };

  const handleCourseSelect = async (course: CourseEntity): Promise<void> => {
    try {
      // Validate that course and course.id exist
      if (!course || !course.id) {
        console.error('Invalid course or missing course ID:', course);
        toast.error('Cannot load course: Missing course ID');
        return;
      }

      // Clear group data since we're coming from All Courses (not from a group)
      dispatch(resetActiveGroupData());

      // Pull course
      const courseData = await UserCourseStorageService.setCourseData(course.id);
      dispatch(setActiveCourseData(courseData));

      // If the name contains "Error", it means we got the fallback data
      if (courseData.name === 'Error Loading Course') {
        toast.error('Could not load course data. Please try again later.');
      }
    } catch (error) {
      console.error('Error selecting course:', error);
      toast.error('Failed to load course. Please try again.');
    }
  };

  return (
    <>
      <div className='flex flex-col fit-content w-full h-full gap-6'>
        <h2>My Courses</h2>
        <CourseCards
          onSelect={handleCourseSelect}
          addCardCondition={userRole === 'enterprise_admin' || userRole === 'instructor'}
          addCardFunc={() => {
            dispatch(setModalVisible(true));
            dispatch(setModalName('CreateCourse'));
          }}
          addCardPrompt='Create Course +'
        />
      </div>
    </>
  );
}
